import { useExplanationSidebar } from "@coffee/contexts";
import { Citation } from "@coffee/data-access/api-types";
import { ClientSecondaryButton } from "@coffee/ui/components/buttons";
import styled from "@emotion/styled";
import { Spacing } from "@unlikelyai-magic/ui/layouts";

const CitationsRow = styled(Spacing)`
  flex-wrap: wrap;
  row-gap: 0.5rem;
`;

type CitationsProps = {
  citations: Citation[];
};

export const Citations = ({ citations }: CitationsProps) => {
  const { setIsOpen, setSelectedClauseId } = useExplanationSidebar();

  const formatCitationHeader = (citationHeader: string) => {
    // Remove the last part of the citation header in square brackets
    // e.g. 1.1.2[Table] -> 1.1.2
    // 6.3.5[^8] -> 6.3.5
    return citationHeader.replace(/\s*\[[^\]]+\]$/, "").trim();
  };

  const hasCitations = citations && citations.length > 0;

  const handleCitationClick = (citation: Citation) => {
    setSelectedClauseId(formatCitationHeader(citation.citation_header));
    setIsOpen(true);
  };

  if (!hasCitations) {
    return null;
  }

  // Remove duplicates based on citation_header
  const uniqueCitations = Array.from(
    new Map(
      citations.map((c) => [formatCitationHeader(c.citation_header), c])
    ).values()
  );

  const sortedCitations = uniqueCitations.sort((a, b) => {
    // Sort by citation_header, prioritizing those that start with a letter (e.g. "Summary" before "1.1.2")
    const aHeader = formatCitationHeader(a.citation_header);
    const bHeader = formatCitationHeader(b.citation_header);
    const aIsAlpha = /^[A-Za-z]/.test(aHeader);
    const bIsAlpha = /^[A-Za-z]/.test(bHeader);

    if (aIsAlpha && !bIsAlpha) return -1;
    if (!aIsAlpha && bIsAlpha) return 1;

    // Note - this will fail if we have something like "1.1.2" and "1.1.2a", or "1.1.8" and "1.1.10"
    // The current document structure does not have such cases, so this is acceptable for now.
    return aHeader.localeCompare(bHeader);
  });

  return (
    <CitationsRow direction="horizontal" gap="xs" justify="flex-start">
      {sortedCitations.map((citation, index) => (
        <ClientSecondaryButton
          key={citation.id || index}
          onClick={() => handleCitationClick(citation)}
        >
          {formatCitationHeader(citation.citation_header)}
        </ClientSecondaryButton>
      ))}
    </CitationsRow>
  );
};
